import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { SbTag, SbButton, SbSpin } from "uiCore";
import { getScoreColor } from "Utils/scoreColor.utils";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import SkillGapList from "./SkillGapList";
import ShortlistTag from "./ShortlistTag";
import IndexTooltip from "./IndexTooltip";
import { ExpandPanelAnimationStyled } from "../talentMobility.animations";

const WrapperStyled = styled(ExpandPanelAnimationStyled)`
  padding: 14px 20px;
  background: ${(p) => p.theme?.colors?.light ?? "#F7F7F7"};
`;

const HeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
`;

const HeaderLeftStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12.5px;
  font-weight: 700;
`;

// Matches the AntD Table `expandable.expandedRowRender` pattern from
// PACGroupAgentDetailsTableV2.jsx — used for both Role Detail's candidate
// rows and Redeployment Center's person rows. `roleId`/`personId` are
// optional — when supplied, a "View Detailed Profile" link opens the full
// Candidate Profile view (Figma: profile card + Skills/Courses tabs) without
// changing this inline view itself. `occupantUserId` (Role Detail only) is
// the role occupant from the URL — carried into the Candidate Profile link
// (as ?user_id=) so that page's own successor-analytics call excludes this
// role's occupants' teammates the same way Role Detail's did; the Shortlist
// action itself no longer needs an occupant id (it's role-scoped, one shared
// shortlist per JD — see CandidateProfileV2.jsx's confirmShortlist).
// `openOccupants` (Role Detail only) is this role's OPEN occupants with their
// own Open/Discoverable snapshot (see useTalentMobilityManagerColumns.jsx's
// openOccupants) — carried as router state (not the URL, it's a list of
// objects) so the opened profile's Shortlist action can forward it to POST
// /talent-mobility/shortlist's `user_ids` batch.
// `currentRoleJdId` (Redeployment Employee View only) is carried as
// ?current_role= so a Shortlist from the opened profile stays a plan draft
// instead of POSTing under the signed-in user's own shortlist row —
// plus ?origin=employee-view, so a Mark as Available on that profile also
// walks the current role's Target Positions down (people-first flow).
// `shortlisted` is the API flag for API-backed rows — passed through to
// ShortlistTag, where a boolean overrides the session store (see the rule in
// ShortlistTag.jsx).
const SkillGapAccordion = ({ title, matchPct, skillGap, roleId, personId, occupantUserId, openOccupants, currentRoleJdId, shortlisted, indexType = "smi", loading = false, skillLimit }) => {
  const navigate = useNavigate();
  const thresholds = useMatchThresholds();
  const indexLabel = indexType === "ssi" ? "SSI" : "SMI";
  return (
    <WrapperStyled>
      <HeaderStyled>
        <HeaderLeftStyled>
          <span>{title}</span>
          {matchPct != null && (
            <IndexTooltip index={indexLabel}>
              <SbTag color={getScoreColor(matchPct, thresholds)}>{matchPct}% {indexLabel}</SbTag>
            </IndexTooltip>
          )}
          {roleId && personId && (
            <ShortlistTag roleId={roleId} personId={personId} shortlisted={shortlisted} style={{ marginLeft: 0 }} />
          )}
        </HeaderLeftStyled>
        {roleId && personId && (
          <SbButton
            buttonType="secondary"
            onClick={() =>
              navigate(
                `/talent-mobility/candidate/${personId}/role/${roleId}?index=${indexType}${
                  occupantUserId ? `&user_id=${occupantUserId}` : ""
                }${currentRoleJdId ? `&current_role=${currentRoleJdId}&origin=employee-view` : ""}`,
                openOccupants?.length ? { state: { openOccupants } } : undefined
              )
            }
          >
            View Detailed Profile
          </SbButton>
        )}
      </HeaderStyled>
      {loading ? <SbSpin size="small" /> : <SkillGapList skillGap={skillGap} showScore={false} limit={skillLimit} />}
    </WrapperStyled>
  );
};

export default SkillGapAccordion;
